import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Shield, AlertTriangle, CheckCircle, ArrowRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { z } from "zod";

const assessmentContactSchema = z.object({
  name: z.string()
    .trim()
    .min(1, "Name is required")
    .max(100, "Name must be less than 100 characters"),
  email: z.string()
    .trim()
    .email("Invalid email address")
    .max(255, "Email must be less than 255 characters")
    .refine((val) => {
      const disposableDomains = ['tempmail.com', 'throwaway.email', '10minutemail.com', 'guerrillamail.com'];
      const domain = val.split('@')[1]?.toLowerCase();
      return !disposableDomains.includes(domain);
    }, "Disposable email addresses are not allowed"),
  consent: z.boolean().refine((val) => val === true, {
    message: "You must consent to data processing",
  }),
});

interface Question {
  id: string;
  question: string;
  category: string;
  options: { value: string; label: string; score: number }[];
}

const questions: Question[] = [
  {
    id: "mfa",
    question: "Do you have multi-factor authentication (MFA) enabled for all user accounts?",
    category: "Access Control",
    options: [
      { value: "all", label: "Yes, for all accounts", score: 10 },
      { value: "some", label: "Only for admin accounts", score: 5 },
      { value: "none", label: "No MFA implemented", score: 0 },
    ],
  },
  {
    id: "risk-assessment",
    question: "When was your last comprehensive security risk assessment?",
    category: "Risk Management",
    options: [
      { value: "recent", label: "Within the last 12 months", score: 10 },
      { value: "old", label: "1-2 years ago", score: 5 },
      { value: "never", label: "Never or more than 2 years ago", score: 0 },
    ],
  },
  {
    id: "backups",
    question: "How often do you test your backup restoration process?",
    category: "Business Continuity",
    options: [
      { value: "regularly", label: "Quarterly or more often", score: 10 },
      { value: "annually", label: "Once per year", score: 5 },
      { value: "never", label: "Never tested", score: 0 },
    ],
  },
  {
    id: "incident-plan",
    question: "Do you have a documented incident response plan?",
    category: "Incident Management",
    options: [
      { value: "tested", label: "Yes, and we've tested it", score: 10 },
      { value: "documented", label: "Yes, but never tested", score: 5 },
      { value: "none", label: "No formal plan", score: 0 },
    ],
  },
  {
    id: "patching",
    question: "How quickly do you apply critical security patches?",
    category: "Vulnerability Management",
    options: [
      { value: "fast", label: "Within 7 days", score: 10 },
      { value: "medium", label: "Within 30 days", score: 5 },
      { value: "slow", label: "No formal patching schedule", score: 0 },
    ],
  },
  {
    id: "training",
    question: "How often do employees receive security awareness training?",
    category: "Security Awareness",
    options: [
      { value: "regular", label: "Quarterly or more often", score: 10 },
      { value: "annual", label: "Annually", score: 5 },
      { value: "none", label: "No regular training", score: 0 },
    ],
  },
  {
    id: "encryption",
    question: "Is sensitive data encrypted both at rest and in transit?",
    category: "Data Protection",
    options: [
      { value: "both", label: "Yes, both at rest and in transit", score: 10 },
      { value: "partial", label: "Only in transit or at rest", score: 5 },
      { value: "none", label: "No encryption", score: 0 },
    ],
  },
  {
    id: "monitoring",
    question: "Do you have 24/7 security monitoring in place?",
    category: "Detection & Response",
    options: [
      { value: "full", label: "Yes, with SOC or SIEM", score: 10 },
      { value: "basic", label: "Basic logging only", score: 5 },
      { value: "none", label: "No monitoring", score: 0 },
    ],
  },
  {
    id: "access-review",
    question: "How often do you review user access rights?",
    category: "Access Control",
    options: [
      { value: "quarterly", label: "Quarterly", score: 10 },
      { value: "annually", label: "Annually", score: 5 },
      { value: "never", label: "Rarely or never", score: 0 },
    ],
  },
  {
    id: "vendor-risk",
    question: "Do you assess the security of third-party vendors?",
    category: "Third-Party Risk",
    options: [
      { value: "formal", label: "Yes, formal vendor risk assessments", score: 10 },
      { value: "informal", label: "Basic due diligence only", score: 5 },
      { value: "none", label: "No vendor assessments", score: 0 },
    ],
  },
];

const Assessment = () => {
  const [currentStep, setCurrentStep] = useState<"intro" | "questions" | "contact" | "results">("intro");
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const progress = (currentQuestion / questions.length) * 100;
  const totalScore = Object.entries(answers).reduce((sum, [questionId, answerId]) => {
    const question = questions.find(q => q.id === questionId);
    const option = question?.options.find(o => o.value === answerId);
    return sum + (option?.score || 0);
  }, 0);
  const maxScore = questions.length * 10;
  const percentageScore = Math.round((totalScore / maxScore) * 100);

  const handleAnswer = (questionId: string, value: string) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setCurrentStep("contact");
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmitContact = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form data
    try {
      assessmentContactSchema.parse({
        name,
        email,
        consent,
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
        return;
      }
    }
    
    setIsSubmitting(true);

    try {
      const recommendations = generateRecommendations();
      
      const { error } = await supabase.from("assessment_results").insert({
        email,
        name,
        score: percentageScore,
        answers: answers,
        recommendations,
      });

      if (error) throw error;

      await supabase.from("leads").insert({
        email,
        name,
        lead_source: "security_assessment",
        lead_type: "assessment",
      });

      setCurrentStep("results");
    } catch (error) {
      console.error("Error saving assessment:", error);
      toast({
        title: "Error",
        description: "There was an issue saving your results. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateRecommendations = (): string[] => {
    const recs: string[] = [];
    
    if (!answers.mfa || answers.mfa !== "all") {
      recs.push("Implement multi-factor authentication across all accounts");
    }
    if (!answers["risk-assessment"] || answers["risk-assessment"] === "never") {
      recs.push("Conduct a comprehensive security risk assessment");
    }
    if (!answers.backups || answers.backups === "never") {
      recs.push("Establish regular backup testing procedures");
    }
    if (!answers["incident-plan"] || answers["incident-plan"] === "none") {
      recs.push("Develop and test an incident response plan");
    }
    if (!answers.patching || answers.patching === "slow") {
      recs.push("Implement faster patch management processes");
    }
    
    return recs;
  };

  const getRiskLevel = (score: number) => {
    if (score >= 80) return { level: "Low Risk", color: "text-green-600", icon: CheckCircle };
    if (score >= 50) return { level: "Moderate Risk", color: "text-yellow-600", icon: AlertTriangle };
    return { level: "High Risk", color: "text-red-600", icon: AlertTriangle };
  };

  if (currentStep === "intro") {
    return (
      <div className="min-h-screen flex flex-col">
        <SEO
          title="Free Cyber Security Assessment | 4FOX Security"
          description="Take our free 2-minute security assessment. Get a personalised report with actionable recommendations to improve your organisation's cyber security posture."
          canonical="https://4fox.uk/assessment"
        />
        <Navbar />
        <section className="gradient-primary pt-24 pb-16 flex-1 flex items-center">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center text-white">
              <Shield className="w-16 h-16 mx-auto mb-6 text-accent" />
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Free Security Assessment
              </h1>
              <p className="text-xl text-white/90 mb-8">
                Answer 10 quick questions to discover your organisation's security posture. 
                Get a personalised report with actionable recommendations.
              </p>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-8">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-3xl font-bold text-accent">10</div>
                    <div className="text-sm text-white/80">Questions</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-accent">2-3</div>
                    <div className="text-sm text-white/80">Minutes</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-accent">FREE</div>
                    <div className="text-sm text-white/80">Report</div>
                  </div>
                </div>
              </div>
              <Button size="lg" onClick={() => setCurrentStep("questions")} className="bg-accent hover:bg-accent/90">
                Start Assessment
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  if (currentStep === "questions") {
    const question = questions[currentQuestion];
    const currentAnswer = answers[question.id];

    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <section className="pt-24 pb-16 bg-secondary/30">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto">
              <div className="mb-8">
                <div className="flex justify-between text-sm text-muted-foreground mb-2">
                  <span>Question {currentQuestion + 1} of {questions.length}</span>
                  <span>{Math.round(progress)}% Complete</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>

              <Card>
                <CardHeader>
                  <CardDescription className="text-accent font-semibold">{question.category}</CardDescription>
                  <CardTitle className="text-2xl">{question.question}</CardTitle>
                </CardHeader>
                <CardContent>
                  <RadioGroup value={currentAnswer} onValueChange={(value) => handleAnswer(question.id, value)}>
                    <div className="space-y-3">
                      {question.options.map((option) => (
                        <div key={option.value} className="flex items-center space-x-3 border rounded-lg p-4 hover:bg-muted/50 cursor-pointer">
                          <RadioGroupItem value={option.value} id={option.value} />
                          <Label htmlFor={option.value} className="flex-1 cursor-pointer">
                            {option.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>

                  <div className="flex gap-4 mt-8">
                    <Button
                      variant="outline"
                      onClick={handleBack}
                      disabled={currentQuestion === 0}
                      className="flex-1"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={handleNext}
                      disabled={!currentAnswer}
                      className="flex-1"
                    >
                      {currentQuestion === questions.length - 1 ? "View Results" : "Next"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  if (currentStep === "contact") {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <section className="pt-24 pb-16 bg-secondary/30">
          <div className="container mx-auto px-4">
            <div className="max-w-md mx-auto">
              <Card>
                <CardHeader>
                  <CardTitle>Get Your Results</CardTitle>
                  <CardDescription>
                    Enter your email to receive your personalised security assessment report.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmitContact} className="space-y-4">
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        required
                      />
                    </div>
                    <div className="flex items-start space-x-2">
                      <Checkbox 
                        id="assessment-consent" 
                        required 
                        checked={consent}
                        onCheckedChange={(checked) => setConsent(checked === true)}
                      />
                      <Label htmlFor="assessment-consent" className="text-sm leading-relaxed cursor-pointer">
                        I consent to 4FOX storing my information and sending me my assessment results. 
                        See our <Link to="/privacy-policy" className="underline hover:text-primary">Privacy Policy</Link>.
                      </Label>
                    </div>
                    <Button type="submit" className="w-full" disabled={isSubmitting || !consent}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        "View My Results"
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  // Results page
  const riskInfo = getRiskLevel(percentageScore);
  const RiskIcon = riskInfo.icon;
  const recommendations = generateRecommendations();

  return (
      <div className="min-h-screen flex flex-col">
      <Navbar />
      <section className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold mb-4">Your Security Assessment Results</h1>
              <p className="text-muted-foreground">Thank you for completing the assessment, {name}!</p>
            </div>

            <Card className="mb-8">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="relative w-32 h-32">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="8" fill="none" className="text-muted" />
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={`${(percentageScore / 100) * 352} 352`}
                        className={percentageScore >= 80 ? "text-green-600" : percentageScore >= 50 ? "text-yellow-600" : "text-red-600"}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-3xl font-bold">{percentageScore}</span>
                    </div>
                  </div>
                </div>
                <CardTitle className="text-2xl flex items-center justify-center gap-2">
                  <RiskIcon className={`w-6 h-6 ${riskInfo.color}`} />
                  <span className={riskInfo.color}>{riskInfo.level}</span>
                </CardTitle>
                <CardDescription>Your overall security score out of 100</CardDescription>
              </CardHeader>
            </Card>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Recommendations</CardTitle>
                <CardDescription>Priority actions to improve your security posture</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {recommendations.map((rec, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-accent text-sm font-semibold">{index + 1}</span>
                      </div>
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gradient-primary text-white">
              <CardHeader>
                <CardTitle className="text-white">Ready to Take Action?</CardTitle>
                <CardDescription className="text-white/80">
                  Book a free consultation to discuss your results and create a personalized security roadmap.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <a 
                  href="https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button size="lg" className="w-full bg-accent hover:bg-accent/90">
                    Book Free Consultation
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Assessment;
